import { Controller } from "@hotwired/stimulus"

// quick-supplier — inline create a Supplier from a modal, then add the
// new supplier as an option in a sibling <select> and select it. Avoids
// the "save form -> nav to /suppliers/new -> nav back -> refill form"
// dance.
//
// Wired on the PO form. Same controller can be reused on other forms
// that need quick-create supplier behavior (e.g. order creation flows
// that learn from a supplier).
//
// Targets:
//   dialog        — the <dialog> to show/close
//   select        — the supplier <select> to append the new option to
//   nameInput     — the name field inside the modal form
//   emailInput    — optional email field
//   phoneInput    — optional phone field
//   error         — div where API errors render
//   submitButton  — modal's create button (disable while submitting)
//
// Values:
//   url  — endpoint to POST the new supplier as JSON (defaults to /suppliers.json)
export default class extends Controller {
    static targets = ["dialog", "select", "nameInput", "emailInput", "phoneInput", "error", "submitButton"]
    static values  = { url: { type: String, default: "/suppliers.json" } }

    open(event) {
        if (event) event.preventDefault()
        this.clearError()
        if (this.hasDialogTarget && typeof this.dialogTarget.showModal === "function") {
            this.dialogTarget.showModal()
        } else if (this.hasDialogTarget) {
            this.dialogTarget.style.display = "block"
        }
        if (this.hasNameInputTarget) {
            // Defer focus until the dialog has actually mounted.
            setTimeout(() => this.nameInputTarget.focus(), 50)
        }
    }

    close(event) {
        if (event) event.preventDefault()
        if (this.hasDialogTarget && typeof this.dialogTarget.close === "function") {
            this.dialogTarget.close()
        } else if (this.hasDialogTarget) {
            this.dialogTarget.style.display = "none"
        }
    }

    async submit(event) {
        if (event) event.preventDefault()
        this.clearError()
        const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
        if (!name) {
            this.showError("Name is required.")
            this.nameInputTarget?.focus()
            return
        }

        this.setBusy(true)
        try {
            const payload = {
                supplier: {
                    name,
                    email: this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : "",
                    phone: this.hasPhoneInputTarget ? this.phoneInputTarget.value.trim() : ""
                }
            }
            const res = await fetch(this.urlValue, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": this.csrfToken()
                },
                body: JSON.stringify(payload)
            })

            if (!res.ok) {
                const data = await this.safeJson(res)
                const msg = (data.errors || []).join(", ") || `Couldn't create supplier (HTTP ${res.status})`
                this.showError(msg)
                return
            }

            const data = await res.json()
            this.appendAndSelectOption(data.id, data.name || name)
            this.resetForm()
            this.close()
        } catch (e) {
            this.showError(`Network error: ${e.message}`)
        } finally {
            this.setBusy(false)
        }
    }

    appendAndSelectOption(id, name) {
        if (!this.hasSelectTarget || !id) return
        const opt = document.createElement("option")
        opt.value = id
        opt.text  = name
        opt.selected = true
        // Insert right after the blank prompt option so the new one is
        // visible at the top of the list.
        const promptIdx = this.selectTarget.options[0]?.value === "" ? 1 : 0
        this.selectTarget.add(opt, promptIdx)
        this.selectTarget.value = id
        // Notify any listening Stimulus controllers / form-validation logic.
        this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || ""
    }

    async safeJson(res) {
        try { return await res.json() } catch { return {} }
    }

    showError(msg) {
        if (!this.hasErrorTarget) return
        this.errorTarget.textContent = msg
        this.errorTarget.style.display = ""
    }

    clearError() {
        if (!this.hasErrorTarget) return
        this.errorTarget.textContent = ""
        this.errorTarget.style.display = "none"
    }

    setBusy(busy) {
        if (!this.hasSubmitButtonTarget) return
        this.submitButtonTarget.disabled = busy
        this.submitButtonTarget.textContent = busy ? "Creating…" : "Create supplier"
    }

    resetForm() {
        if (this.hasNameInputTarget)  this.nameInputTarget.value  = ""
        if (this.hasEmailInputTarget) this.emailInputTarget.value = ""
        if (this.hasPhoneInputTarget) this.phoneInputTarget.value = ""
    }
}
